/**
 * @fileoverview Hidden user message convention: <%...%> and metadata.hidden
 *
 * @purpose Ensure trigger/system messages (e.g. <%User entered the chat%>) are never
 *          shown or sent to the user. Used by KorvoChat (UI) and messages API (response).
 *
 * @ownership KorvoChat component infrastructure
 */

/**
 * Matches any content between <% and %>. Such messages are never shown to the user.
 */
export const HIDDEN_CONTENT_PATTERN = /<%.*?%>/

/** Content part (e.g. OpenAI multimodal { type: 'text', text: string }). */
export type ContentPart = { type?: string; text?: string; [key: string]: unknown }

export type MessageLike = {
  role: string
  content?: string | ContentPart[] | null
  metadata?: Record<string, unknown> | null
}

/** Returns concatenated text from content (string or array of parts with .text). */
export function getMessageContentText(content: MessageLike['content']): string {
  if (content == null) return ''
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) return ''
  return content
    .map((part) => (part && typeof part === 'object' && typeof (part as ContentPart).text === 'string' ? (part as ContentPart).text : ''))
    .join('')
}

/**
 * Returns true if the message should be hidden from the user:
 * - role !== 'user' → not hidden
 * - metadata.hidden === true → hidden
 * - content matches HIDDEN_CONTENT_PATTERN (<%...%>) → always hidden
 * - content matches optional hiddenPattern (string or RegExp) → hidden
 */
export function isHiddenUserMessage(
  message: MessageLike,
  hiddenPattern?: RegExp | string
): boolean {
  if (message.role !== 'user') return false
  if (message.metadata?.hidden === true) return true
  const content = getMessageContentText(message.content)
  if (HIDDEN_CONTENT_PATTERN.test(content)) return true
  if (!hiddenPattern || !content) return false
  if (typeof hiddenPattern === 'string') {
    return content.trim() === hiddenPattern
  }
  return hiddenPattern.test(content)
}

/**
 * Redacts user messages that match the hidden pattern so they are never sent to the client.
 * Returns a new message with content set to '' and metadata.hidden = true when content matches.
 */
export function redactHiddenMessage<T extends MessageLike>(message: T): T {
  if (message.role !== 'user') return message
  const contentText = getMessageContentText(message.content)
  if (!HIDDEN_CONTENT_PATTERN.test(contentText)) return message
  return {
    ...message,
    content: '',
    metadata: { ...(message.metadata ?? {}), hidden: true },
  }
}

/** Replaces <%...%> placeholders in strings so they are never sent to the client. */
const HIDDEN_PLACEHOLDER_PATTERN = /<%.*?%>/g
const HIDDEN_REPLACEMENT = '[hidden]'

/**
 * Recursively replaces <%...%> in strings so GET messages response never contains unredacted placeholders.
 * Used after redactHiddenMessage so content and metadata (e.g. agentDebug.systemPrompt) are safe.
 */
export function stripHiddenPlaceholders(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.replace(HIDDEN_PLACEHOLDER_PATTERN, HIDDEN_REPLACEMENT)
  }
  if (Array.isArray(value)) {
    return value.map(stripHiddenPlaceholders)
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = stripHiddenPlaceholders(v)
    }
    return out
  }
  return value
}
